<template>
  <section class="article-heading grid">
    <h1 class="title t-h1" v-html="nbsp(article.title)" />
    <div class="info">
      <p v-if="article.author" class="row">
        <span class="t-p-s-130 c-t-3">{{ t('article.author') }}</span>
        <NuxtLink
          v-if="article.author.slug"
          :to="
            localePath({
              name: 'person-slug',
              params: { slug: article.author.slug }
            })
          "
          class="t-p-m-130"
          >{{ article.author.name }}</NuxtLink
        >
        <span v-else class="t-p-m-130"> {{ article.author.name }}</span>
      </p>
      <p class="row">
        <span class="t-p-s-130 c-t-3">{{ t('article.publication-date') }}</span>
        <span v-if="article.publication_date" class="t-p-m-130">{{
          formatDate(article.publication_date, 'D.MM.YYYY')
        }}</span>
      </p>
    </div>
    <div class="header-image">
      <BaseImage
        :image="article.image"
        :width="1280"
        class="fill-space"
        :height="675"
        :img-sizes="[
          { w: 375, h: 198 },
          { w: 768, h: 405 },
          { w: 1280, h: 675 },
          { w: 1920, h: 1013 }
        ]"
        size="(min-width: 753px) 50vw, 100vw"
        :lazyload="false"
      />
    </div>
    <div class="content">
      <ArticleTableOfContents
        v-if="article.contents.length"
        :contents="article.contents"
      />
      <div
        class="middle-col"
        :class="{ 'middle-item': !article.contents.length }"
      >
        <ArticleTags v-if="article.tags.length" :tags="article.tags" />
        <div class="wysiwyg" v-html="nbsp(article.lead)" />
      </div>
    </div>
  </section>
</template>

<script lang="ts" setup>
import type { PublicArticleDetail } from '@/service/__generated-api'

defineProps<{
  article: PublicArticleDetail
}>()

const localePath = useLocalePath()

const { t } = useI18n()
</script>

<style lang="scss" scoped>
.title {
  @include column(xs, 12);
  margin-bottom: rem(22px);
  color: $layout-text-02;
}

.info {
  @include column(xs, 12);
  @include flex-col;
  gap: rem(6px);
  margin-bottom: rem(40px);

  @include media-up(md) {
    flex-direction: row;
    gap: rem(32px);
  }
}

.header-image {
  @include column(xs, 12);
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1;
  margin-bottom: rem(73px);
  overflow: hidden;
  border-radius: rem(14px);

  @include media-up(s-lg) {
    aspect-ratio: 2 / 1;
  }
}

.content {
  @include column(xs, 12);
  @include flex-col;
  display: grid;
  grid-template-columns: 100%;
  gap: rem(16px);
  width: 100%;
  max-width: rem(740px);
  margin-inline: auto;
  margin-bottom: rem(70px);

  @include media-up(lg) {
    grid-template-columns: 20% 60% 20%;
    align-items: flex-start;
    max-width: unset;
    min-height: rem(365px);
    margin-inline: unset;
  }
}

.middle-item {
  @include media-up(lg) {
    grid-column: 2 / 3;
  }
}

.middle-col {
  @include flex-col;
  gap: rem(32px);

  &:not(:has(.article-tags)) {
    margin-top: rem(76px);
  }
}

.wysiwyg {
  &:deep(h1),
  &:deep(h2),
  &:deep(h3),
  &:deep(h4),
  &:deep(h5),
  &:deep(h6),
  &:deep(p),
  &:deep([data-tag='p']),
  &:deep(ul) {
    margin-bottom: rem(32px);
  }
}

.row {
  display: flex;
  gap: rem(10px);
  align-items: baseline;
}

.c-t-3 {
  color: $layout-text-03;
}
</style>
